<template lang="pug">
  management-page.scrollable(title="Summary"
                :creatable="false"
                :searchable="false"
                :showHeader="false")
    template(v-slot:body)
      aiq-row(:gutter="16" justify="end")
          .time-range-filter
            aiq-date-picker(v-model="dateRange"
                          @change="handleDatePickerChange($event)"
                          type="daterange"
                          placeholder="Select date range",
                          popper-class="metrics-time-range__date-picker-popup"
                          :shortcuts="datePickerOptions.shortcuts"
                          :disabled-date="datePickerOptions.disabledDate"
                          :teleported="false"
                          format="YYYY/MM/DD"
                          :clearable="false")
      .chart-grid
        aiq-row(:gutter="16")
          aiq-col(:span="24")
            overview-chart(v-if="timezone"
                          :dateRange="dateRangeUtc"
                          :chartConfig="messagesByChannelsConfig"
                          grouped=true
                          @onChartBreakdownChange="chosenMsgByChannelBucketOption=$event"
                          visualization='aiq-bar-chart'
                          :downloadWithTimeTicks="true"
                          :reportFileName="returnReportFileName('Messages By Channel')"
                          :style="{ height: '100%' }"
                          :useDebounceForLoadMetrics="true"
                          deriveLinesFromResponse
                          derivedLineType='bar'
                          :getOriginalData="getOriginalData"
                          @onChartDataChange="makeXtoLabelMapping($event)"
                          :formatXTick="xLabelsMsgByChannelsFormat"
                          @onXLabelsChange="xLabelsMessagesByChannel=$event"
                          :shouldHandlePartialDates="true"
                          )

</template>

<script>
import
{ mapGetters } from 'vuex';
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import OverviewChart from '@/components/Chart.vue';
import { createPickerDateRangeOption, datePickerDefaultOptions, lastAvailableMonthAndQuarterRange, dstDateRangeUtc } from '@/pages/Metrics/configs/dateRange';
import _, { cloneDeep } from 'lodash';
import messagesByChannelsConfig from '../../configs/messagesByChannels';
import activePlatformCustomersChartConfig from '../../configs/activePlatformCustomersChartConfig';
import PlatformFiltersMixin from '@/pages/Metrics/Platform/children/selectableFilters/SelectableFilters';
import dayjs from '@/localPlugins/dayjs';
const xAxisFormatForActiveCustomers = (value) => dayjs(value).format('MMM DD');
import { useTimeZone } from '@/libs';
const LOAD_METRICS_DEBOUNCE_TIME = 100;


export default {
  pageResource: '/metrics/platform',
  name: 'Messages',
  mixins: [PlatformFiltersMixin],
  components: {
    ManagementPage,
    OverviewChart,
  },
  data() {
    return {
      timezone: null,
      messagesByChannelsConfig,
      chosenMsgByChannelBucketOption: null,
      xLabelsMsgByChannels: {},
      activePlatformCustomersChartConfig,
      xAxisFormatForActiveCustomers,
    };
  },
  computed: {
    ...mapGetters({
      configs: 'configs/graphsConfigs',
    }),
    datePickerOptions() {
      return {
        ...datePickerDefaultOptions(),
        shortcuts: lastAvailableMonthAndQuarterRange.map(createPickerDateRangeOption),
      };
    },
    dateRangeUtc() {
      return dstDateRangeUtc(this.dateRange);
    },
  },
  async mounted() {
    this.timezone = await useTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      this.$store.dispatch,
      this.$store._state.data.configs,
    );
    this.debouncedLoadMetric = _.debounce(this.loadMetric, LOAD_METRICS_DEBOUNCE_TIME);
  },
  methods: {
    loadMetric() {
      return Promise.resolve();
    },
    xLabelsMsgByChannelsFormat(date) {
      const dateMoment = dayjs(date).tz(this.timezone).valueOf();
      //eslint-disable-next-line
      return this.xLabelsMsgByChannels[dateMoment] + `${this.isDataPartial(dateMoment) ? ' *' : ''}` ;
    },
    returnReportFileName(chartName = 'data') {
      const [startTimestamp, endTimestamp] = this.dateRange;
      const startDate = dayjs.isDayjs(startTimestamp)
        ? startTimestamp.format('YYYY-MM-DD')
        : dayjs(startTimestamp).format('YYYY-MM-DD');
      const endDate = dayjs.isDayjs(endTimestamp)
        ? endTimestamp.format('YYYY-MM-DD')
        : dayjs(endTimestamp).format('YYYY-MM-DD');
      return `${chartName.replaceAll(' ', '_')}_${startDate}_${endDate}`;
    },

    makeXtoLabelMapping(data) {
      this.xLabelsMsgByChannels = {};
      this.chartData = data?.dataset?.[0]?.data_points || [];
      this.originalData = cloneDeep(data);
      this.chartData.forEach((data_point) => {
        const dateMoment = dayjs(data_point.x).tz(this.timezone);
        let value = dateMoment.valueOf();
        let label = dateMoment.format('MMM DD');
        if (this.chosenMsgByChannelBucketOption?.value === 'week') {
          value = dateMoment.day(0).valueOf();
          // change the x value for the time series so the bars get space correctly.
          data_point.x = value;
        } else if (this.chosenMsgByChannelBucketOption?.value === 'month') {
          value = dateMoment.startOf('month').valueOf();
          data_point.x = value;
          label = dateMoment.format('MMM');
        }
        this.xLabelsMsgByChannels[value] = label;
      });

    },
    isDataPartial(date) {
      const timeStamp = new Date(date).getTime();
      const isPartial = this.chartData.find(item => item.x === timeStamp)?.isPartial;
      return isPartial;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/aiq-variables.scss";

.scrollable {
  overflow-y: auto;
}

.el-card:not(:first-of-type) {
  margin-top: 24px;
}

.time-range-filter {
  position: relative;
  margin-bottom: 24px;
}

:deep(.c3-circles-total_convos) {
opacity: 0 !important;
}

.chart-configuration {
  display: flex;
  margin-bottom: 16px;
  justify-content: space-between;
  .breakdown {
    display: flex;
    align-items: center;
    flex-grow: 1;
  }
  .members-filter {
    display: flex;
    align-items: center;
  }

  .export-command {
    display: flex;
    height: 30px;
    width: 40px;
    justify-content: center;
    align-items: center;
    margin-left: 16px;
    padding: 0;
  }
}
</style>
